import React from 'react'
import { useWatch } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { NumberField, Select } from 'src/Libs/inputs/react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { OptionalFields } from 'src/Libs/flow/components'
import { ChargingWeeklySchedule } from '..'
import { getChargingStationChargingModeOptions } from '../../utils'

type EvseType = 'eebus' | 'modbusTcp'

interface CommonEvseChargingSettingsProps {
  evseType: EvseType
  index: number
  deviceId: string
  canEdit: boolean
}

export const CommonEvseChargingSettings: React.FC<CommonEvseChargingSettingsProps> = ({ evseType, index, deviceId, canEdit }) => {
  const { t } = useCustomTranslation()
  const { control } = useInstallationForm()
  const fieldPrefix = `chargingStations.${evseType}.${index}` as `chargingStations.eebus.${number}` | `chargingStations.modbusTcp.${number}`
  const chargingMode = useWatch({ control, name: `${fieldPrefix}.chargingMode` })
  const chargingStrategyDescription = getChargingStationChargingModeOptions(t).find(option => option.value === chargingMode)?.description
  const showSolarRatio = chargingMode === 'pvImmediate' || chargingMode === 'pvDelayed' || chargingMode === 'pvOnly'
  const showSafePvCalendar = chargingMode === 'pvImmediate'
  const showEfficientPvCalendar = chargingMode === 'pvDelayed'

  return (
    <AccordionItem depth={1} title={t('installation.evseEditor.chargingStrategy.title')} expanded elevation={0}>
      <SettingsBox
        title={t('installation.evseEditor.chargingStrategy.title')}
        description={chargingStrategyDescription}
        suffix={
          <Select
            name={`${fieldPrefix}.chargingMode`}
            options={getChargingStationChargingModeOptions(t)}
            label={t('installation.evseEditor.chargingStrategy.title')}
            disabled={!canEdit}
          />
        }
        depth={1}
      />
      <SettingsBox
        title={t('installation.evseEditor.holdingTime')}
        suffix={
          <NumberField
            label={t('installation.evseEditor.holdingTime')}
            suffix={t('affixes.minute')}
            name={`${fieldPrefix}.holdingTime`}
            min={1}
            max={60}
            disabled={!canEdit}
          />
        }
        depth={1}
      />
      <OptionalFields<InstallationFormModel> condition={showSolarRatio} fieldNames={[`${fieldPrefix}.solarRatio`]}>
        <SettingsBox
          title={t('installation.evseEditor.min-current-sun-mode-subTitle')}
          description={t('installation.evseEditor.min-current-sun-mode-description')}
          suffix={
            <NumberField
              label={t('installation.evseEditor.min-current-sun-mode')}
              suffix={t('affixes.percent')}
              name={`${fieldPrefix}.solarRatio`}
              min={0}
              max={100}
              disabled={!canEdit}
            />
          }
          depth={1}
        />
      </OptionalFields>

      <OptionalFields<InstallationFormModel> condition={showSafePvCalendar} fieldNames={[`${fieldPrefix}.safePvSchedule`]}>
        <ChargingWeeklySchedule evseDeviceId={deviceId} evseType={evseType} index={index} scheduleType="safePvSchedule" />
      </OptionalFields>

      <OptionalFields<InstallationFormModel> condition={showEfficientPvCalendar} fieldNames={[`${fieldPrefix}.efficientPvSchedule`]}>
        <ChargingWeeklySchedule evseDeviceId={deviceId} evseType={evseType} index={index} scheduleType="efficientPvSchedule" />
      </OptionalFields>
    </AccordionItem>
  )
}
